import { useEffect, useState } from "react";
import { API } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { Copy, ExternalLink, Lock } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const inputClass =
  "w-full input-line py-3 text-[15px] placeholder:text-[#FAF9F6]/35";
const labelClass =
  "block text-[10px] tracking-[0.22em] uppercase font-semibold text-[#C5A880] mb-2";

function formatEur(v) {
  try {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(v);
  } catch {
    return `€ ${v}`;
  }
}

function Login({ onOk }) {
  const [key, setKey] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!key) return;
    onOk(key);
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="max-w-sm w-full bg-[#111111] border border-[#FAF9F6]/10 p-10"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3 mb-8">
          <Lock size={16} className="text-[#C5A880]" />
          <div className="overline">Area riservata</div>
        </div>
        <label className={labelClass}>Chiave amministratore</label>
        <input
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          className={inputClass}
          placeholder="••••••••"
          data-testid="admin-key-input"
        />
        <button
          type="submit"
          className="mt-8 w-full btn-primary py-3 text-[12px] tracking-[0.22em] uppercase font-semibold"
          data-testid="admin-login-submit"
        >
          Accedi
        </button>
      </form>
    </div>
  );
}

function CreateQuote({ adminKey, onCreated }) {
  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    description: "",
    amount: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState("");

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const amount = parseFloat(form.amount);
      if (!amount || amount <= 0) {
        toast.error("Importo non valido");
        setLoading(false);
        return;
      }
      const { data } = await axios.post(
        `${API}/admin/quotes`,
        { ...form, amount, currency: "eur" },
        { headers: { "X-Admin-Key": adminKey } }
      );
      const url = `${window.location.origin}/pay/${data.id}`;
      setLink(url);
      toast.success("Preventivo creato");
      onCreated?.();
      setForm({
        client_name: "",
        client_email: "",
        description: "",
        amount: "",
        notes: "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#111111] border border-[#FAF9F6]/10 p-8">
      <h3 className="font-display text-xl mb-6">Nuovo preventivo</h3>
      <form
        onSubmit={submit}
        className="grid sm:grid-cols-2 gap-x-6 gap-y-5"
        data-testid="admin-quote-form"
      >
        <div>
          <label className={labelClass}>Cliente *</label>
          <input
            required
            value={form.client_name}
            onChange={handle("client_name")}
            className={inputClass}
            data-testid="admin-quote-client-name"
          />
        </div>
        <div>
          <label className={labelClass}>Email cliente *</label>
          <input
            required
            type="email"
            value={form.client_email}
            onChange={handle("client_email")}
            className={inputClass}
            data-testid="admin-quote-client-email"
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass}>Descrizione *</label>
          <textarea
            required
            rows={3}
            value={form.description}
            onChange={handle("description")}
            className={`${inputClass} resize-none`}
            data-testid="admin-quote-description"
          />
        </div>
        <div>
          <label className={labelClass}>Importo (EUR) *</label>
          <input
            required
            type="number"
            step="0.01"
            min="1"
            value={form.amount}
            onChange={handle("amount")}
            className={inputClass}
            placeholder="500.00"
            data-testid="admin-quote-amount"
          />
        </div>
        <div>
          <label className={labelClass}>Note interne</label>
          <input
            value={form.notes}
            onChange={handle("notes")}
            className={inputClass}
            data-testid="admin-quote-notes"
          />
        </div>
        <div className="sm:col-span-2">
          <button
            type="submit"
            disabled={loading}
            className="btn-primary px-6 py-3 text-[12px] tracking-[0.22em] uppercase font-semibold disabled:opacity-60"
            data-testid="admin-quote-submit"
          >
            {loading ? "Creazione..." : "Crea preventivo"}
          </button>
        </div>
      </form>

      {link && (
        <div
          className="mt-6 p-4 bg-[#0A0A0A] border border-[#C5A880]/40"
          data-testid="admin-quote-link"
        >
          <div className="overline mb-2">Link di pagamento</div>
          <div className="flex items-center gap-3">
            <code className="text-xs text-[#FAF9F6]/80 break-all flex-1">
              {link}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(link);
                toast.success("Copiato");
              }}
              className="text-[#C5A880] hover:text-[#D5B890]"
              data-testid="admin-quote-copy"
            >
              <Copy size={14} />
            </button>
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              className="text-[#C5A880] hover:text-[#D5B890]"
            >
              <ExternalLink size={14} />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function ArticlesAdmin({ adminKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = {
    title: "",
    slug: "",
    excerpt: "",
    tag: "",
    cover_image: "",
    content: "",
    published: true,
  };
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/articles`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handle = (k) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const startEdit = (a) => {
    setEditing(a.id);
    setForm({
      title: a.title || "",
      slug: a.slug || "",
      excerpt: a.excerpt || "",
      tag: a.tag || "",
      cover_image: a.cover_image || "",
      content: a.content || "",
      published: a.published !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setEditing(null);
    setForm(empty);
  };

  const onCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      toast.error("La copertina deve essere max 4MB");
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, cover_image: String(reader.result || "") }));
      setUploading(false);
    };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) {
      toast.error("Titolo e contenuto sono obbligatori");
      return;
    }
    try {
      const payload = { ...form };
      if (!payload.slug) delete payload.slug;
      if (editing) {
        await axios.put(`${API}/admin/articles/${editing}`, payload, {
          headers: { "X-Admin-Key": adminKey },
        });
        toast.success("Articolo aggiornato");
      } else {
        await axios.post(`${API}/admin/articles`, payload, {
          headers: { "X-Admin-Key": adminKey },
        });
        toast.success("Articolo pubblicato");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Eliminare "${a.title}"? Operazione non annullabile.`))
      return;
    try {
      await axios.delete(`${API}/admin/articles/${a.id}`, {
        headers: { "X-Admin-Key": adminKey },
      });
      toast.success("Articolo eliminato");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#111111] border border-[#FAF9F6]/10 p-8">
        <div className="flex items-baseline justify-between mb-6">
          <h3 className="font-display text-xl">
            {editing ? "Modifica approfondimento" : "Nuovo approfondimento"}
          </h3>
          {editing && (
            <button
              onClick={resetForm}
              className="text-xs text-[#FAF9F6]/50 hover:text-[#C5A880]"
              data-testid="admin-article-cancel-edit"
            >
              Annulla modifica
            </button>
          )}
        </div>
        <form
          onSubmit={submit}
          className="grid sm:grid-cols-2 gap-x-6 gap-y-5"
          data-testid="admin-article-form"
        >
          <div className="sm:col-span-2">
            <label className={labelClass}>Titolo *</label>
            <input
              required
              value={form.title}
              onChange={handle("title")}
              className={inputClass}
              placeholder="Es. Commento a Cass. Civ. n. 12345/2025"
              data-testid="admin-article-title"
            />
          </div>
          <div>
            <label className={labelClass}>Tag / Categoria (libera)</label>
            <input
              value={form.tag}
              onChange={handle("tag")}
              className={inputClass}
              placeholder="Es. Sentenza · M&A · Normativa"
              data-testid="admin-article-tag"
            />
          </div>
          <div>
            <label className={labelClass}>
              Slug URL (opzionale, altrimenti auto)
            </label>
            <input
              value={form.slug}
              onChange={handle("slug")}
              className={inputClass}
              placeholder="commento-cass-civ-12345-2025"
              data-testid="admin-article-slug"
            />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Estratto (2-3 righe)</label>
            <textarea
              rows={2}
              value={form.excerpt}
              onChange={handle("excerpt")}
              className={`${inputClass} resize-none`}
              data-testid="admin-article-excerpt"
            />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>
              Copertina (URL o upload, opzionale)
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                value={form.cover_image}
                onChange={handle("cover_image")}
                className={inputClass}
                placeholder="URL immagine o base64"
                data-testid="admin-article-cover-url"
              />
              <label
                htmlFor="admin-article-cover-file"
                className="flex items-center gap-3 border border-dashed border-[#FAF9F6]/20 hover:border-[#C5A880] cursor-pointer px-4 py-2 text-xs text-[#FAF9F6]/60"
              >
                {uploading ? "Caricamento..." : "Carica file (max 4MB)"}
                <input
                  id="admin-article-cover-file"
                  type="file"
                  accept="image/*"
                  onChange={onCover}
                  className="hidden"
                  data-testid="admin-article-cover-input"
                />
              </label>
            </div>
            {form.cover_image && (
              <div className="mt-3 flex items-center gap-3">
                <img
                  src={form.cover_image}
                  alt="anteprima copertina"
                  className="h-16 w-24 object-cover border border-[#FAF9F6]/10"
                />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, cover_image: "" })}
                  className="text-xs text-[#FAF9F6]/50 hover:text-[#C5A880]"
                >
                  Rimuovi
                </button>
              </div>
            )}
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>
              Contenuto (Markdown supportato) *
            </label>
            <textarea
              required
              rows={14}
              value={form.content}
              onChange={handle("content")}
              className={`${inputClass} resize-y font-mono text-[13px] leading-relaxed`}
              placeholder={`# Sezione\nTesto in **grassetto**, *corsivo*, [link](https://…).\n\n- Punto 1\n- Punto 2\n\n> Citazione`}
              data-testid="admin-article-content"
            />
            <div className="mt-2 text-[11px] text-[#FAF9F6]/40">
              Supporta headings (#, ##), grassetto, corsivo, elenchi, citazioni,
              tabelle, link.
            </div>
          </div>
          <div className="sm:col-span-2 flex items-center gap-3 pt-2">
            <input
              id="art-pub"
              type="checkbox"
              checked={form.published}
              onChange={handle("published")}
              className="accent-[#C5A880]"
              data-testid="admin-article-published"
            />
            <label
              htmlFor="art-pub"
              className="text-xs text-[#FAF9F6]/70 cursor-pointer"
            >
              Pubblicato (visibile agli utenti)
            </label>
          </div>
          <div className="sm:col-span-2 flex justify-end gap-3 pt-4">
            <button
              type="submit"
              className="btn-primary px-6 py-3 text-[12px] tracking-[0.22em] uppercase font-semibold"
              data-testid="admin-article-submit"
            >
              {editing ? "Salva modifiche" : "Pubblica articolo"}
            </button>
          </div>
        </form>
      </div>

      <Section title="Elenco approfondimenti" count={items.length}>
        {loading && <p className="text-sm text-[#FAF9F6]/50 py-4">Caricamento…</p>}
        {!loading && items.length === 0 && (
          <p className="text-sm text-[#FAF9F6]/50 py-4">
            Nessun articolo ancora pubblicato.
          </p>
        )}
        <div className="divide-y divide-[#FAF9F6]/10">
          {items.map((a) => (
            <div
              key={a.id}
              className="py-4 grid grid-cols-12 gap-3 items-center text-sm"
              data-testid={`admin-article-row-${a.id}`}
            >
              <div className="col-span-6">
                <div className="text-[#FAF9F6] font-medium">{a.title}</div>
                <div className="text-xs text-[#FAF9F6]/50">
                  /{a.slug} · {a.tag || "—"}
                </div>
              </div>
              <div className="col-span-2 text-xs text-[#FAF9F6]/60">
                {a.published ? (
                  <span className="text-emerald-400">Pubblicato</span>
                ) : (
                  <span className="text-[#FAF9F6]/40">Bozza</span>
                )}
              </div>
              <div className="col-span-2 text-xs text-[#FAF9F6]/50">
                {new Date(a.updated_at).toLocaleDateString("it-IT")}
              </div>
              <div className="col-span-2 text-right space-x-3">
                <button
                  onClick={() => startEdit(a)}
                  className="text-xs text-[#C5A880] hover:underline"
                  data-testid={`admin-article-edit-${a.id}`}
                >
                  Modifica
                </button>
                <a
                  href={`/approfondimenti/${a.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[#FAF9F6]/60 hover:text-[#C5A880]"
                >
                  Vedi
                </a>
                <button
                  onClick={() => remove(a)}
                  className="text-xs text-red-400 hover:text-red-300"
                  data-testid={`admin-article-delete-${a.id}`}
                >
                  Elimina
                </button>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <div className="bg-[#111111] border border-[#FAF9F6]/10 p-6 lg:p-8">
      <div className="flex items-baseline justify-between mb-6">
        <h3 className="font-display text-xl">{title}</h3>
        <span className="overline">{count} totali</span>
      </div>
      {children}
    </div>
  );
}

export default function Admin() {
  const [adminKey, setAdminKey] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (key) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/dashboard`, {
        headers: { "X-Admin-Key": key },
      });
      setData(res.data);
      setAdminKey(key);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Chiave non valida");
    } finally {
      setLoading(false);
    }
  };

  if (!adminKey) return <Login onOk={load} />;

  return (
    <div className="min-h-screen bg-carbon text-warm">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="flex items-baseline justify-between mb-12">
          <div>
            <div className="overline mb-2">Admin Dashboard</div>
            <h1 className="font-display text-3xl lg:text-4xl">
              Ricciardi Advisory
            </h1>
          </div>
          <button
            onClick={() => load(adminKey)}
            className="text-xs text-[#C5A880] hover:text-[#D5B890]"
            data-testid="admin-refresh"
          >
            {loading ? "Aggiornamento..." : "Aggiorna"}
          </button>
        </div>

        <Tabs defaultValue="articles">
          <TabsList className="bg-transparent border-b border-[#FAF9F6]/10 rounded-none p-0 h-auto mb-8 w-full justify-start gap-6">
            {["articles", "quotes", "appointments", "opinions", "contacts", "transactions"].map(
              (t) => (
                <TabsTrigger
                  key={t}
                  value={t}
                  className="rounded-none bg-transparent border-b-2 border-transparent data-[state=active]:border-[#C5A880] data-[state=active]:text-[#C5A880] px-0 pb-3 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#FAF9F6]/50"
                  data-testid={`admin-tab-${t}`}
                >
                  {t === "articles" ? "Approfondimenti" : t}
                </TabsTrigger>
              )
            )}
          </TabsList>

          <TabsContent value="articles">
            <ArticlesAdmin adminKey={adminKey} />
          </TabsContent>

          <TabsContent value="quotes" className="space-y-6">
            <CreateQuote adminKey={adminKey} onCreated={() => load(adminKey)} />
            <Section
              title="Preventivi"
              count={data?.quotes?.length || 0}
            >
              <div className="divide-y divide-[#FAF9F6]/10">
                {(data?.quotes || []).map((q) => (
                  <div
                    key={q.id}
                    className="py-4 grid grid-cols-12 gap-4 items-center text-sm"
                  >
                    <div className="col-span-3">
                      <div className="text-[#FAF9F6]">{q.client_name}</div>
                      <div className="text-xs text-[#FAF9F6]/50">
                        {q.client_email}
                      </div>
                    </div>
                    <div className="col-span-4 text-[#FAF9F6]/70 text-xs">
                      {q.description}
                    </div>
                    <div className="col-span-2 font-display text-[#C5A880]">
                      {formatEur(q.amount)}
                    </div>
                    <div className="col-span-1">
                      <span
                        className={`text-xs ${q.status === "paid" ? "text-emerald-400" : "text-[#FAF9F6]/60"}`}
                      >
                        {q.status}
                      </span>
                    </div>
                    <div className="col-span-2 text-right">
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}/pay/${q.id}`;
                          navigator.clipboard.writeText(url);
                          toast.success("Link copiato");
                        }}
                        className="text-xs text-[#C5A880] hover:underline"
                      >
                        Copia link
                      </button>
                    </div>
                  </div>
                ))}
                {(!data?.quotes || data.quotes.length === 0) && (
                  <p className="text-sm text-[#FAF9F6]/50 py-4">
                    Nessun preventivo.
                  </p>
                )}
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="appointments">
            <Section
              title="Richieste appuntamento"
              count={data?.appointments?.length || 0}
            >
              <div className="divide-y divide-[#FAF9F6]/10">
                {(data?.appointments || []).map((a) => (
                  <div key={a.id} className="py-4 text-sm">
                    <div className="flex justify-between">
                      <div>
                        <div>{a.full_name}</div>
                        <div className="text-xs text-[#FAF9F6]/50">
                          {a.email} · {a.phone || "-"} · {a.company || "-"}
                        </div>
                      </div>
                      <div className="text-xs text-[#FAF9F6]/50 text-right">
                        {a.preferred_date} {a.preferred_time}
                        <div>{a.topic}</div>
                      </div>
                    </div>
                    {a.notes && (
                      <p className="mt-2 text-xs text-[#FAF9F6]/60">
                        {a.notes}
                      </p>
                    )}
                  </div>
                ))}
                {(!data?.appointments || data.appointments.length === 0) && (
                  <p className="text-sm text-[#FAF9F6]/50 py-4">
                    Nessuna richiesta.
                  </p>
                )}
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="opinions">
            <Section
              title="Richieste parere"
              count={data?.opinions?.length || 0}
            >
              <div className="divide-y divide-[#FAF9F6]/10">
                {(data?.opinions || []).map((o) => (
                  <div key={o.id} className="py-4 text-sm">
                    <div className="flex justify-between">
                      <div>
                        <div>{o.full_name}</div>
                        <div className="text-xs text-[#FAF9F6]/50">
                          {o.email} · {o.area} · {o.urgency}
                        </div>
                      </div>
                      <div className="text-xs text-[#FAF9F6]/50">
                        {(o.attachments || []).length} file
                      </div>
                    </div>
                    <div className="mt-2">
                      <strong className="text-[#C5A880]">{o.subject}</strong>
                    </div>
                    <p className="mt-1 text-xs text-[#FAF9F6]/60">
                      {o.description}
                    </p>
                  </div>
                ))}
                {(!data?.opinions || data.opinions.length === 0) && (
                  <p className="text-sm text-[#FAF9F6]/50 py-4">
                    Nessuna richiesta.
                  </p>
                )}
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="contacts">
            <Section
              title="Messaggi contatti"
              count={data?.contacts?.length || 0}
            >
              <div className="divide-y divide-[#FAF9F6]/10">
                {(data?.contacts || []).map((c) => (
                  <div key={c.id} className="py-4 text-sm">
                    <div className="flex justify-between">
                      <div>
                        <div>{c.full_name}</div>
                        <div className="text-xs text-[#FAF9F6]/50">
                          {c.email} · {c.phone || "-"} · {c.company || "-"}
                        </div>
                      </div>
                      <div className="text-xs text-[#FAF9F6]/50">
                        {(c.attachments || []).length} file
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-[#FAF9F6]/70">
                      {c.message}
                    </p>
                  </div>
                ))}
                {(!data?.contacts || data.contacts.length === 0) && (
                  <p className="text-sm text-[#FAF9F6]/50 py-4">
                    Nessun messaggio.
                  </p>
                )}
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="transactions">
            <Section
              title="Transazioni Stripe"
              count={data?.transactions?.length || 0}
            >
              <div className="divide-y divide-[#FAF9F6]/10">
                {(data?.transactions || []).map((t) => (
                  <div
                    key={t.session_id}
                    className="py-4 grid grid-cols-12 gap-4 items-center text-sm"
                  >
                    <div className="col-span-4 font-mono text-xs text-[#FAF9F6]/70">
                      {t.session_id?.slice(0, 24)}...
                    </div>
                    <div className="col-span-3 font-display text-[#C5A880]">
                      {formatEur(t.amount)}
                    </div>
                    <div className="col-span-2 text-xs">{t.status}</div>
                    <div className="col-span-3 text-xs">
                      <span
                        className={
                          t.payment_status === "paid"
                            ? "text-emerald-400"
                            : "text-[#FAF9F6]/60"
                        }
                      >
                        {t.payment_status}
                      </span>
                    </div>
                  </div>
                ))}
                {(!data?.transactions || data.transactions.length === 0) && (
                  <p className="text-sm text-[#FAF9F6]/50 py-4">
                    Nessuna transazione.
                  </p>
                )}
              </div>
            </Section>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
